import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Typography,
  Space,
  Alert,
  Descriptions,
  Radio, // New import for demoStatus
  Modal // New import for failure modal
} from 'antd';
import { Spin } from 'antd';
import {
  BankOutlined,
  CheckCircleOutlined,
  RocketOutlined,

} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom'; // For navigation
import CardBindingProgress from './CardBindingProgress'; // New component import

const { Text } = Typography;

interface StepContentProps {
  current: number;
  onNext: () => void;
  onPrev: () => void;
}

// 步骤3：银行卡绑定
const CardBindingStep: React.FC<StepContentProps> = ({ onNext, onPrev }) => { // Added onPrev
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [bindingStatus, setBindingStatus] = useState<'form' | 'reminder' | 'progress_page'>('form'); // Modified states
  const [verificationAmount, setVerificationAmount] = useState(() => {
    return (Math.random() * 0.98 + 0.01).toFixed(2);
  });
  const [demoStatus, setDemoStatus] = useState<'progress' | 'success' | 'failure'>('progress'); // New state for demo
  const [completing, setCompleting] = useState(false); // 完成打款验证loading
  const [regenLoading, setRegenLoading] = useState(false); // 重新生成金额loading
  const [processing, setProcessing] = useState(false); // 全局处理转圈

  const navigate = useNavigate(); // For navigation to account overview

  React.useEffect(() => {
    form.setFieldsValue({
      bind_acct_no_acct_nme: '上海数据交易所有限公司'
    });
  }, [form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('银行卡信息提交成功！');
      setBindingStatus('reminder'); // Transition to reminder state
    } catch (error) {
      message.error('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteBinding = () => {
    if (demoStatus === 'success') {
      setProcessing(true);
      setCompleting(true);
      setTimeout(() => {
        setCompleting(false);
        setProcessing(false);
        Modal.success({
          title: '银行卡绑定成功',
          content: '开户完成，现在可以使用账户中心的所有功能。',
          okText: '确认',
          onOk: () => navigate('/account/overview')
        });
      }, 1000);
    } else if (demoStatus === 'failure') {
      Modal.error({
        title: '银行卡绑定失败',
        content: (
          <>
            <p>打款金额不符或银行卡信息有误，请核对后重新尝试。</p>
            <p>常见失败原因：打款金额与指定金额不一致，或银行卡状态异常。</p>
          </>
        ),
        okText: '我知道了'
      });
    } else {
      setBindingStatus('progress_page'); // 进入进度页
    }
  };

  const handleRegenerateAmount = () => {
    setProcessing(true);
    setRegenLoading(true);
    setTimeout(() => {
      const newAmount = (Math.random() * 0.98 + 0.01).toFixed(2);
      setVerificationAmount(newAmount);
      setRegenLoading(false);
      setProcessing(false);
      message.success('已生成新的打款验证金额！');
    }, 800);
  };

  // Render CardBindingProgress component if in progress_page state
  if (bindingStatus === 'progress_page') {
    return (
      <CardBindingProgress
        onSuccess={() => Modal.success({ title: '银行卡绑定成功', content: '开户完成，现在可以使用账户中心的所有功能。', okText: '确认', onOk: () => navigate('/account/overview') })}
        onReauthorize={() => setBindingStatus('form')} // On reauthorize, go back to form
        onReturnToReminder={() => setBindingStatus('reminder')} // Go back to reminder page
      />
    );
  }

  // Render reminder page if in reminder state
  if (bindingStatus === 'reminder') {
    return (
      <Card className="enterprise-step-card enterprise-fade-in" style={{ width: '800px', margin: '24px auto 0 auto' }}>
        <div className="enterprise-step-card-header" style={{ background: '#1890ff', color: '#ffffff', padding: '16px 24px', borderRadius: '8px 8px 0 0' }}>
          <BankOutlined className="enterprise-step-icon" style={{ fontSize: '20px', marginRight: '8px' }} />
          <span style={{ fontSize: '18px', fontWeight: 600 }}>第三步：银行卡绑定</span>
        </div>

        <div style={{ padding: '32px', position: 'relative' }}>
          {/* Floating Demo Status Selector */}
          <div style={{ position: 'fixed', top: 120, right: 20, zIndex: 1000, background: 'rgba(255, 255, 255, 0.9)', padding: '12px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', border: '1px solid #d9d9d9' }}>
            <div style={{marginBottom: 8, fontWeight: 600}}>状态切换（演示）</div>
            <Radio.Group
              value={demoStatus}
              onChange={(e: any) => setDemoStatus(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="progress">处理中</Radio.Button>
              <Radio.Button value="success">成功</Radio.Button>
              <Radio.Button value="failure">失败</Radio.Button>
            </Radio.Group>
          </div>

          <Spin spinning={processing}>
          <Descriptions title="请完成打款验证以激活银行卡" bordered column={1} labelStyle={{ width: '20%' }}>
            <Descriptions.Item label="收款账户名称">
              上海数据交易所有限公司
              <Button size="small" style={{marginLeft: 8}} onClick={() => {
                navigator.clipboard.writeText('上海数据交易所有限公司');
                message.success('账户名称已复制');
              }}>复制</Button>
            </Descriptions.Item>
            <Descriptions.Item label="收款账号">
              310904000018800001234
              <Button size="small" style={{marginLeft: 8}} onClick={() => {
                navigator.clipboard.writeText('310904000018800001234');
                message.success('账号已复制');
              }}>复制</Button>
            </Descriptions.Item>
            <Descriptions.Item label="开户银行">交通银行上海分行营业部</Descriptions.Item>
            <Descriptions.Item label={<Text strong style={{color: '#1890ff'}}>打款金额</Text>}>
              <Text strong style={{color: '#1890ff', fontSize: 18}}>¥{verificationAmount} 元</Text>
              <Button size="small" style={{marginLeft: 8}} onClick={() => {
                navigator.clipboard.writeText(verificationAmount);
                message.success('打款金额已复制');
              }}>复制金额</Button>
            </Descriptions.Item>
          </Descriptions>

          <Alert
            message="重要提醒：请严格按以下要求进行打款验证"
            description={
              <div style={{ display: 'grid', gap: 8, fontSize: 14, color: '#595959', lineHeight: 1.7 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#faad14' }}>•</span>
                  <span>
                    请在
                    <strong style={{ color: '#d48806', fontWeight: 600 }}> 48 小时内 </strong>
                    使用您绑定的银行卡向上述账户转入
                    <strong style={{ color: '#1890ff', fontWeight: 600 }}> 指定金额 ¥{verificationAmount}</strong>。
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#faad14' }}>•</span>
                  <span>
                    <strong style={{ color: '#d4380d', fontWeight: 600 }}>金额必须与指定金额完全一致</strong>，任何差异（包含小数位）都会导致验证失败。
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#faad14' }}>•</span>
                  <span>
                    转账完成后，系统将自动进行核验并
                    <strong style={{ color: '#389e0d', fontWeight: 600 }}> 激活银行卡</strong>。
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#faad14' }}>•</span>
                  <span>
                    若验证失败或金额过期，可点击
                    <strong style={{ color: '#0958d9', fontWeight: 600 }}> 重新生成打款金额</strong>
                    获取新的验证金额后重试。
                  </span>
                </div>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginTop: 24 }}
          />


          <div className="enterprise-action-buttons" style={{ marginTop: '32px', textAlign: 'center' }}>
            <Space>
              <Button
                type="primary"
                onClick={handleCompleteBinding}
                className="enterprise-primary-button"
                size="large"
                icon={<CheckCircleOutlined />}
                style={{ minWidth: 160 }}
                loading={completing}
              >
                我已完成打款验证
              </Button>
              <Button
                onClick={handleRegenerateAmount}
                icon={<RocketOutlined />}
                size="large"
                style={{ minWidth: 140 }}
                loading={regenLoading}
              >
                重新生成打款金额
              </Button>
              <Button
                onClick={() => setBindingStatus('form')} // Back to form
                icon={<RocketOutlined />}
                size="large"
                style={{ minWidth: 140 }}
              >
                返回重新绑卡
              </Button>
            </Space>
          </div>
          </Spin>
        </div>
      </Card>
    );
  }

  // Render form if in form state
  return (
    <Card className="enterprise-step-card enterprise-fade-in" style={{ width: '800px', margin: '24px auto 0 auto' }}>
      <div className="enterprise-step-card-header" style={{ background: '#1890ff', color: '#ffffff', padding: '16px 24px', borderRadius: '8px 8px 0 0' }}>
        <BankOutlined className="enterprise-step-icon" style={{ fontSize: '20px', marginRight: '8px' }} />
        <span style={{ fontSize: '18px', fontWeight: 600 }}>第三步：银行卡绑定</span>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className="enterprise-form"
        style={{ padding: '24px', margin: '0 auto' }}
      >
        <div style={{ marginBottom: 0, padding: 0, background: 'transparent' }}>

          <Alert
            message="银行卡绑定说明"
            description="请确保银行卡信息准确无误，特别是开户支行名称和联行号，以免影响绑定。"
            type="info"
            showIcon
            className="enterprise-alert"
            style={{ marginBottom: 24 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bind_acct_no_acct_nme"
                label="户名"
                rules={[{ required: true, message: '请输入户名' }]}
              >
                <Input placeholder="请输入户名" disabled size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bind_acct_no"
                label="银行卡号"
                rules={[{ required: true, message: '请输入银行卡号' }]}
              >
                <Input placeholder="请输入银行卡号" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bind_acct_bank_no"
                label="开户行联行号"
                rules={[{ required: true, message: '请输入开户行联行号' }]}
              >
                <Input placeholder="请输入开户行联行号" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bind_acct_bank_nme"
                label="开户支行"
                rules={[{ required: true, message: '请输入开户支行' }]}
              >
                <Input placeholder="请输入开户支行" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="enterprise-action-buttons" style={{ textAlign: 'center', marginTop: 32 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<BankOutlined />}
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: 600,
              padding: '0 40px',
              background: '#1890ff',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)'
            }}
          >
            {loading ? '提交中...' : '提交银行卡信息'}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default CardBindingStep;
